import type { AnnotationStats } from "../api/ml";
import type { Project } from "../api/types";
import { MIN_REVIEWED } from "../lib/training";
import { Icon, type IconName } from "./icons";

export type NavId = "images" | "annotate" | "augment" | "train" | "models" | "auto" | "classes" | "export" | "admin";

interface Item { id: NavId; label: string; icon: IconName; badge?: string; tip: string; pro?: boolean }

/** The project's pages, with live counts, so the nav also says where the project stands. */
function items(stats: AnnotationStats | null, augmentOpen: boolean): Item[] {
  const review = stats?.auto_annotated ?? 0;
  const copies = stats?.augmented_reviewed_image_count ?? 0;
  return [
    { id: "images", label: "Images", icon: "image", badge: stats ? String(stats.total) : undefined, tip: "Upload, filter and select images" },
    { id: "annotate", label: "Annotate", icon: "box", badge: review ? `${review} to review` : undefined, tip: "Draw and review annotations" },
    { id: "augment", label: "Augment", icon: "sparkles", badge: copies ? `${copies} copies` : undefined, pro: !augmentOpen,
      tip: augmentOpen ? "Make more training images from the ones you have, when there are few, or few of one class"
        : "Make more training images from the ones you have. Part of Model Bench Pro." },
    { id: "train", label: "Train", icon: "target", tip: "Train a model on the reviewed annotations" },
    { id: "models", label: "Models", icon: "cube", badge: stats?.latest_model_version ? `v${stats.latest_model_version}` : undefined, tip: "Every version: download, export for deployment, import weights" },
    { id: "auto", label: "Auto-annotate", icon: "wand", badge: stats?.unannotated ? `${stats.unannotated} left` : undefined, tip: "Let the trained model annotate the rest" },
  ];
}
const SETUP: Item[] = [
  { id: "classes", label: "Classes", icon: "tag", tip: "Add, rename, recolour and merge classes" },
  { id: "export", label: "Export dataset", icon: "download", tip: "Download the images and annotations (COCO, YOLO, Pascal VOC)" },
];

/**
 * The left navigation inside a project. On the annotation page it collapses to icons, so the canvas keeps its width;
 * the labels are then tooltips.
 */
export function SideNav({ project, stats, active, compact, isAdmin, canEdit, augmentOpen = true, onGo }: {
  project: Project | null; stats: AnnotationStats | null; active: NavId | null; compact: boolean; isAdmin: boolean; canEdit: boolean;
  /** Whether the edition has Augment (Pro); the item stays, tagged Pro, so the step is visible either way. */
  augmentOpen?: boolean;
  onGo: (id: NavId) => void;
}) {
  const row = (it: Item, disabled: boolean, why?: string) => (
    <button key={it.id} className="nav-item" aria-current={active === it.id ? "page" : undefined} disabled={disabled}
      title={disabled ? why : compact ? `${it.label}${it.badge ? ` · ${it.badge}` : ""}` : it.tip} aria-label={compact ? it.label : undefined}
      onClick={() => onGo(it.id)} data-testid={`nav-${it.id}`}>
      <Icon name={it.icon} size={18} />
      {!compact && <span className="nav-label">{it.label}</span>}
      {!compact && it.badge && <span className="nav-badge">{it.badge}</span>}
      {!compact && it.pro && <span className="pill pro">Pro</span>}
      {compact && it.badge && it.id === "annotate" && <span className="nav-dot" aria-hidden="true" />}
    </button>
  );
  const noProject = !project;
  const reviewed = stats?.reviewed_annotation_count ?? 0;
  return (
    <nav className={`sidenav${compact ? " compact" : ""}`} aria-label="Project" data-testid="sidenav">
      {!compact && <div className="nav-caption">{project ? "Project" : "No project selected"}</div>}
      {items(stats, augmentOpen).map((it) => row(it, noProject || (!canEdit && (it.id === "train" || it.id === "auto")),
        noProject ? "Select a project first" : "Your role can look at projects but not change them"))}
      {!compact && <div className="nav-caption">Setup</div>}
      {compact && <div className="nav-sep" />}
      {SETUP.map((it) => row(it, noProject || (it.id === "classes" && !canEdit), noProject ? "Select a project first" : "Your role can look at projects but not change them"))}
      <div className="nav-fill" />
      {isAdmin && row({ id: "admin", label: "Administration", icon: "shield", tip: "Users, invitations, credits and the audit log" }, false)}
      {!compact && stats && (
        <div className="nav-progress" data-testid="nav-progress">
          {stats.can_train ? <>
            <span>Ready to train</span>
            <div className="bar"><i style={{ width: "100%" }} /></div>
            <span className="muted">{reviewed} reviewed annotations</span>
          </> : <>
            <span>Reviewed annotations</span>
            <div className="bar"><i style={{ width: `${(Math.min(reviewed, MIN_REVIEWED) / MIN_REVIEWED) * 100}%` }} /></div>
            <span className="muted">{reviewed} / {MIN_REVIEWED} needed to train</span>
          </>}
        </div>
      )}
    </nav>
  );
}
